import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";

import { PromptLibraryItem, PromptLibraryItemSource } from "@/modules/promptLibrary/promptLibrary.types";
import { FormInput } from "@/components/forms/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useCreatePromptLibraryItemsMutation,
  useGetSuggestedPromptLibraryItemNameQuery,
  useUpdatePromptLibraryItemsMutation,
} from "@/modules/promptLibrary/promptLibrary.api";
import { FormTextArea } from "@/components/forms/textarea";

type Props =
  | {
      isOpen: boolean;
      mode: "add";
      setIsOpen: (isOpen: boolean) => void;
      item?: Pick<PromptLibraryItem, "prompt" | "source">;
    }
  | {
      isOpen: boolean;
      item: PromptLibraryItem;
      mode: "edit";
      setIsOpen: (isOpen: boolean) => void;
    };

export type ModifyNewPromptLibraryItemDialogMode = "add" | "edit";

interface FormInputs {
  name: string;
  prompt: string;
  notes: string;
  source: PromptLibraryItemSource;
}

export const ModifyNewPromptLibraryItemDialog: FC<Props> = ({ isOpen, setIsOpen, item, mode }) => {
  const [createPromptLibraryItem] = useCreatePromptLibraryItemsMutation();
  const [updatePromptLibraryItemsMutation] = useUpdatePromptLibraryItemsMutation();

  const methods = useForm<FormInputs>({
    defaultValues: {
      name: item && "name" in item ? item.name : "",
      prompt: item?.prompt,
      notes: item && "notes" in item ? item.notes : "",
      source: item && "source" in item ? item.source : "qa",
    },
  });
  const { data, isLoading } = useGetSuggestedPromptLibraryItemNameQuery(item?.prompt, {
    skip: !item?.prompt || mode !== "add",
  });

  useEffect(() => {
    if (!data || mode !== "add") {
      return;
    }
    methods.setValue("name", data);
  }, [data]);

  const onSubmit = async (payload: FormInputs) => {
    if (mode === "add") {
      await createPromptLibraryItem(payload);
    } else {
      await updatePromptLibraryItemsMutation({ id: item.id, ...payload });
    }

    methods.reset();
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-xl">
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader className="mb-5">
              <DialogTitle>{mode === "add" ? "Add" : "Edit"} Prompt</DialogTitle>
            </DialogHeader>

            <div className="mb-6 flex flex-col gap-4">
              <div>
                <label htmlFor="prompt" className="mb-3 text-sm font-medium">
                  Prompt
                </label>
                <FormTextArea
                  {...methods.register("prompt", { required: "This field is required" })}
                  placeholder="Type prompt improve"
                  name="prompt"
                />
              </div>

              <div className="flex gap-x-4">
                <div className="w-full">
                  <label htmlFor="name" className="mb-3 text-sm font-medium">
                    Name
                  </label>
                  <FormInput
                    {...methods.register("name", { required: "This field is required" })}
                    name="name"
                    placeholder="Enter name"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="source" className="mb-3 text-sm font-medium">
                    Source
                  </label>
                  <select
                    {...methods.register("source", { required: true })}
                    disabled={!!item?.source && mode === "add"}
                    className="block h-[40px] w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
                  >
                    <option value="qa">Q&A</option>
                    <option value="open_chat">Open Chat</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="notes" className="mb-3 text-sm font-medium">
                  Notes
                </label>
                <FormTextArea
                  {...methods.register("notes", { required: "This field is required" })}
                  placeholder="Add any additional notes here..."
                  name="notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
